import { invariant } from "@searchui-searchforms/shared/utils";
import type { GivenNameExactness, SurnameExactness } from "@/entities/data-model";

export type NameExactnessId = keyof GivenNameExactness["flags"] | keyof SurnameExactness["flags"];

const getGivenNameMatchLabelKey = (flags: GivenNameExactness["flags"]): string => {
    const map: Record<string, string> = {
        "000": "name.exactness.exact",
        "100": "name.exactness.exactAndSoundsLike",
        "110": "name.exactness.exactSoundsLikeSimilar",
        "101": "name.exactness.exactSoundsLikeInitials",
        "111": "name.exactness.exactSoundsLikeSimilarInitials",
        "010": "name.exactness.exactAndSimilar",
        "011": "name.exactness.exactSimilarInitials",
        "001": "name.exactness.exactAndInitials",
    };

    const key = `${Number(flags.phonetic)}${Number(flags.similar)}${Number(flags.initials)}`;
    const labelKey = map[key];

    invariant(labelKey, "labelKey must exist");

    return labelKey;
};

const getSurnameMatchLabelKey = (flags: SurnameExactness["flags"]): string => {
    const map: Record<string, string> = {
        "000": "name.exactness.exact",
        "100": "name.exactness.exactAndSoundsLike",
        "110": "name.exactness.exactSoundsLikeSimilar",
        "101": "name.exactness.exactSoundsLikeSoundex",
        "111": "name.exactness.exactSoundsLikeSimilarSoundex",
        "010": "name.exactness.exactAndSimilar",
        "011": "name.exactness.exactSimilarSoundex",
        "001": "name.exactness.exactAndSoundex",
    };

    const key = `${Number(flags.phonetic)}${Number(flags.similar)}${Number(flags.soundex)}`;
    const labelKey = map[key];

    invariant(labelKey, "labelKey must exist");

    return labelKey;
};

export const getMatchLabelKey = (flags: GivenNameExactness["flags"] | SurnameExactness["flags"]): string => {
    if ("initials" in flags) {
        return getGivenNameMatchLabelKey(flags);
    }
    return getSurnameMatchLabelKey(flags);
};

export const getOptionIds = (flags: GivenNameExactness["flags"] | SurnameExactness["flags"]) => {
    if ("initials" in flags) {
        return ["phonetic", "similar", "initials"] as const;
    }
    return ["phonetic", "similar", "soundex"] as const;
};

export const idToTranslationKey = {
    phonetic: "name.exactness.soundsLike",
    similar: "name.exactness.similar",
    initials: "name.exactness.initials",
    soundex: "name.exactness.soundex",
} as const satisfies Record<NameExactnessId, string>;
